import { Typography } from '@mui/material';
import TSButton from 'components/common/Button/Button';
import TSInfoAlert from 'components/common/InfoAlert/InfoAlert';
import MLToast from 'components/common/Toast/Toast';
import usePushNotifications from 'hooks/usePushNotifications';

// A plain <a href download> is ignored by browsers for cross-origin URLs
// (S3 is a different origin) — fetching the file and triggering a
// synthetic click on an object URL is what actually forces a download
// instead of just opening the PDF in a new tab.
const downloadSafetySheet = async (url, showToast) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'safety-sheet.pdf';
    link.click();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    showToast('Could not download the safety sheet right now.', 'error');
  }
};

const LogSuccessMsg = ({ handleDismiss, safetySheetUrl }) => {
  const { permission, subscribe } = usePushNotifications();
  const { showToast } = MLToast();

  const hasNotificationAction =
    permission === 'default' || permission === 'denied';
  const hasAnythingToDo = hasNotificationAction || !!safetySheetUrl;

  return (
    <div className="flex flex-col items-center justify-center gap-4 m-6 p-6 border-4 border-[var(--ts-primary)] rounded-2xl">
      <Typography variant="h2">You are checked in!</Typography>
      <Typography variant="body1">
        Enjoy your adventure and stay safe!
      </Typography>

      {hasAnythingToDo ? (
        <>
          <Typography variant="h3">
            Before you get going on your trail
          </Typography>

          {permission === 'default' && (
            <>
              <Typography variant="h5">
                So we can send a reminder if you forget to check out, allow
                notifications. We will only send notifications for safety
                reminders, no fluff!
              </Typography>
              <TSButton
                btnText="Allow notifications"
                btnVariant="outlined"
                classList="py-4"
                onClickFunc={subscribe}
              />
            </>
          )}

          {permission === 'denied' && (
            <TSInfoAlert
              info1="Checkout reminder notifications are blocked for this browser"
              info2="Your browser may have blocked them automatically. Enable notifications for this site in your browser's site settings to receive reminders here instead of by SMS."
              alertType="warning-alert"
              alertIcon="Warning"
            />
          )}

          {safetySheetUrl && (
            <>
              <Typography variant="h5">
                We have a safety sheet for you! It details key safety
                information that could be useful while on your trail.
              </Typography>
              <TSButton
                btnText="Download Safety PDF"
                btnVariant="outlined"
                classList="py-4"
                onClickFunc={() =>
                  downloadSafetySheet(safetySheetUrl, showToast)
                }
              />
            </>
          )}
        </>
      ) : (
        <Typography variant="body1">
          You're all set. Enjoy your trail.
        </Typography>
      )}

      <div className="flex gap-4 mt-4">
        <TSButton
          btnText="I'm on my way!"
          classList="py-4"
          onClickFunc={handleDismiss}
        />
      </div>
    </div>
  );
};

export default LogSuccessMsg;
